import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

interface BypassCheckFieldProps {
	/**
	 * Unique DOM id base for the switch and its description
	 * (e.g. "bypass-journey-check").
	 */
	id: string;
	/**
	 * i18n keyPrefix within the `appointments-form` namespace holding the
	 * `label` and `description` strings for this bypass toggle.
	 */
	keyPrefix: string;
	checked: boolean;
	onCheckedChange: (value: boolean) => void;
	disabled?: boolean;
	/**
	 * Layout to match the sibling toggle on each page:
	 * - "inline": switch on the left (matches Enable Payment Gateway on create &
	 *   Send Email Notification in the bulk reschedule modal).
	 * - "between": switch on the right (matches Send Email Notification on the
	 *   single reschedule page).
	 */
	variant?: "inline" | "between";
}

/**
 * Admin-gated control for the appointment "bypass" toggles that let staff skip a
 * server-side availability check (slot overlap, therapist contract window, travel
 * feasibility) by sending the matching `bypass_*` flag to the backend. Renders
 * nothing for non-ADMIN users; the gate is UX-only — the server remains the
 * authority. Uses a warning (amber) palette to flag the bypass risk while
 * matching the page's toggle style.
 */
export function BypassCheckField({
	id,
	keyPrefix,
	checked,
	onCheckedChange,
	disabled,
	variant = "inline",
}: BypassCheckFieldProps) {
	const { t } = useTranslation("appointments-form", { keyPrefix });

	const descriptionId = `${id}-description`;
	const label = (
		<Label
			htmlFor={id}
			className="flex items-center gap-1.5 text-amber-900 dark:text-amber-200"
		>
			<AlertTriangle className="size-4 shrink-0 text-amber-600 dark:text-amber-500" />
			{t("label")}
		</Label>
	);
	const description = (
		<p
			id={descriptionId}
			className="text-pretty text-xs text-amber-800/80 dark:text-amber-300/80"
		>
			{t("description")}
		</p>
	);

	if (variant === "between") {
		return (
			<div className="flex w-full flex-row items-center justify-between gap-3 rounded-md border border-amber-500/50 bg-amber-50/50 p-3 shadow-inner col-span-full dark:border-amber-900/50 dark:bg-amber-950/20">
				<div className="space-y-1">
					{label}
					{description}
				</div>
				<Switch
					id={id}
					checked={checked}
					onCheckedChange={onCheckedChange}
					disabled={disabled}
					aria-describedby={descriptionId}
					className="!mt-0"
				/>
			</div>
		);
	}

	return (
		<div className="relative flex w-full items-start gap-2 rounded-md border border-amber-500/50 bg-amber-50/50 p-4 shadow-xs outline-none col-span-full dark:border-amber-900/50 dark:bg-amber-950/20">
			<Switch
				id={id}
				checked={checked}
				onCheckedChange={onCheckedChange}
				disabled={disabled}
				aria-describedby={descriptionId}
				className="data-[state=checked]:[&_span]:rtl:-translate-x-2 order-1 h-4 w-6 after:absolute after:inset-0 [&_span]:size-3 data-[state=checked]:[&_span]:translate-x-2"
			/>
			<div className="grid grow gap-2">
				{label}
				{description}
			</div>
		</div>
	);
}
